// Lógica pura (sin React/Redux) para armar y calcular items de inventario.
// Se mantiene separada de los componentes para poder probarla con scripts
// standalone, igual que se hizo con reportsLogic.jsx.

const VALOR_PRODUCTO_COMPLETO = "__PRODUCTO_COMPLETO__";

function esCampoSeleccionable(campo) {
  return (
    campo &&
    (campo.tipoCampo === "Select" || campo.tipoCampo === "option") &&
    Array.isArray(campo.valor)
  );
}

// Identidad estable de una fuente, independiente de cómo se haya llegado a
// ella (campo general, campo dentro de un grupo, o campo anidado en un
// producto). Se usa para deduplicar: el mismo iDCampo/producto no debe
// contarse dos veces aunque aparezca listado por más de un lado (ej. un
// campo que vive tanto en "formulario" como repetido en "gruposFormularios").
function identidadFuente(fuente) {
  return [fuente.origen, fuente.iDCampo || "", fuente.codigoProducto || ""].join("|");
}

export function deduplicarFuentes(fuentes) {
  const vistos = new Set();
  const resultado = [];
  (fuentes || []).forEach((f) => {
    const id = identidadFuente(f);
    if (!vistos.has(id)) {
      vistos.add(id);
      resultado.push(f);
    }
  });
  return resultado;
}

// Para las fuentes de tipo "producto" (producto completo, sin desglose),
// resuelve la(s) key(s) de productosAdicionales correspondientes — se usa
// para poder escribir agotadoInventario en el producto directamente desde
// el panel al guardar, sin esperar al backend.
export function productosDeFuentes(fuentes, registryConfiguration) {
  const productos = (registryConfiguration && registryConfiguration.productosAdicionales) || {};
  const resultado = [];
  (fuentes || []).forEach((fuente) => {
    if (fuente.origen !== "producto") return;
    for (var key in productos) {
      if (productos[key] && productos[key].codigoProducto === fuente.codigoProducto) {
        resultado.push(key);
      }
    }
  });
  return resultado;
}

// Recorre inscripcionesConfiguracion (formulario, gruposFormularios,
// productosAdicionales) y arma la lista de "fuentes" que se pueden ligar a
// un item de inventario: campos Select sueltos, productos completos, y
// campos Select anidados dentro de un producto (vía su grupo referenciado).
export function listarFuentesDisponibles(registryConfiguration) {
  const fuentes = [];
  const config = registryConfiguration || {};

  for (var k in config.formulario || {}) {
    const campo = config.formulario[k];
    if (esCampoSeleccionable(campo)) {
      fuentes.push({
        key: "campo|" + campo.iDCampo,
        origen: "campo",
        iDCampo: campo.iDCampo,
        etiqueta: "Campo: " + campo.etiqueta,
        valores: campo.valor,
      });
    }
  }

  for (var grupoKey in config.gruposFormularios || {}) {
    const grupo = config.gruposFormularios[grupoKey];
    for (var gk in grupo || {}) {
      const campo = grupo[gk];
      if (esCampoSeleccionable(campo)) {
        fuentes.push({
          key: "campo|" + campo.iDCampo,
          origen: "campo",
          iDCampo: campo.iDCampo,
          etiqueta: "Campo (" + grupoKey + "): " + campo.etiqueta,
          valores: campo.valor,
        });
      }
    }
  }

  for (var prodKey in config.productosAdicionales || {}) {
    const producto = config.productosAdicionales[prodKey];
    fuentes.push({
      key: "producto|" + producto.codigoProducto,
      origen: "producto",
      codigoProducto: producto.codigoProducto,
      etiqueta: 'Producto completo: "' + producto.nombreProducto + '"',
      valores: null,
    });

    // formularioProducto puede venir como el nombre del grupo (string, hay
    // que buscarlo en gruposFormularios) o ya como el grupo de campos
    // resuelto (objeto/arreglo) — ambos formatos existen en producción
    // según cómo se haya creado/editado el producto, se soportan los dos.
    const grupoDelProducto =
      typeof producto.formularioProducto === "string"
        ? (config.gruposFormularios && config.gruposFormularios[producto.formularioProducto]) || null
        : producto.formularioProducto || null;
    for (var pk in grupoDelProducto || {}) {
      const campo = grupoDelProducto[pk];
      if (esCampoSeleccionable(campo)) {
        fuentes.push({
          key: "productoCampo|" + producto.codigoProducto + "|" + campo.iDCampo,
          origen: "productoCampo",
          codigoProducto: producto.codigoProducto,
          iDCampo: campo.iDCampo,
          etiqueta:
            'Producto "' + producto.nombreProducto + '" — ' + campo.etiqueta,
          valores: campo.valor,
        });
      }
    }
  }

  return deduplicarFuentes(fuentes);
}

// Por cada fuente seleccionada, arma las filas crudas {fuenteKey, rawValor,
// etiquetaFuente, etiquetaValor} a mapear hacia una opción canónica.
export function listarFilasDeMapeo(fuentesSeleccionadas) {
  const filas = [];
  fuentesSeleccionadas.forEach((fuente) => {
    if (fuente.origen === "producto") {
      filas.push({
        fuenteKey: fuente.key,
        rawValor: VALOR_PRODUCTO_COMPLETO,
        etiquetaFuente: fuente.etiqueta,
        etiquetaValor: "(producto completo, sin opciones)",
      });
      return;
    }
    (fuente.valores || []).forEach((v) => {
      filas.push({
        fuenteKey: fuente.key,
        rawValor: v.valor,
        etiquetaFuente: fuente.etiqueta,
        etiquetaValor: v.texto,
      });
    });
  });
  return filas;
}

export { VALOR_PRODUCTO_COMPLETO };

// Convierte el arreglo de opciones editado en memoria al formato que se
// guarda en Firebase, conservando vendidos/agotado de las opciones que ya
// existían (se identifican por nombre).
export function construirOpcionesParaGuardar(opcionesEditadas, opcionesPrevias) {
  const previasPorNombre = {};
  for (var k in opcionesPrevias || {}) {
    const p = opcionesPrevias[k];
    previasPorNombre[p.nombre] = p;
  }

  const resultado = {};
  opcionesEditadas.forEach((op, i) => {
    const previa = previasPorNombre[op.nombre];
    resultado["opcion_" + (i + 1)] = {
      nombre: op.nombre,
      capacidad: Number(op.capacidad) || 0,
      vendidos: previa ? previa.vendidos || 0 : 0,
      agotado: previa ? previa.agotado === true : false,
      valoresOrigen: op.valoresOrigen.reduce((acc, v) => {
        acc[v] = true;
        return acc;
      }, {}),
    };
  });
  return resultado;
}

// Recorre los inscritos del evento y recalcula cuántas unidades de cada
// opción de cada item de inventario están realmente vendidas (statusPago
// === "ok"). Es la misma regla que se plantea para el backend (Fase 2), pero
// corrida en el cliente contra los datos ya cargados en el panel.
export function recalcularVendidosDesdeInscritos(inventarios, eventParticipants) {
  const resultado = {};
  for (var itemKey in inventarios || {}) {
    const item = inventarios[itemKey];
    // Deduplicado defensivo: protege items guardados antes de este fix (o
    // con datos corregidos a mano) que puedan tener la misma fuente dos
    // veces, lo que duplicaría el conteo por cada inscrito.
    const fuentesUnicas = deduplicarFuentes(item.fuentes);
    const conteos = {};
    for (var opcionKey in item.opciones || {}) {
      conteos[opcionKey] = 0;
    }

    for (var inscritoKey in eventParticipants || {}) {
      const inscrito = eventParticipants[inscritoKey];
      // "borrado" es un borrado lógico (el registro sigue existiendo con
      // borrado:true) — un inscrito borrado no debe contar aunque siga
      // marcado como pagado.
      if (
        !inscrito ||
        (inscrito.statusPago && inscrito.statusPago.status) !== "ok" ||
        inscrito.borrado === true
      ) {
        continue;
      }

      fuentesUnicas.forEach((fuente) => {
        let rawValor = null;
        let cantidad = 1;

        if (fuente.origen === "campo") {
          rawValor = inscrito[fuente.iDCampo];
        } else if (fuente.origen === "producto" || fuente.origen === "productoCampo") {
          const orden = Array.isArray(inscrito.order) ? inscrito.order : [];
          const linea = orden.find(
            (o) => o && o.code === "producto-" + fuente.codigoProducto
          );
          if (linea) {
            cantidad = Number(linea.quantity) || 1;
            rawValor =
              fuente.origen === "producto"
                ? VALOR_PRODUCTO_COMPLETO
                : linea.extraData && linea.extraData[fuente.iDCampo];
          }
        }

        if (rawValor === null || rawValor === undefined) return;

        for (var opcionKey in item.opciones || {}) {
          const opcion = item.opciones[opcionKey];
          if (opcion.valoresOrigen && opcion.valoresOrigen[rawValor]) {
            conteos[opcionKey] = (conteos[opcionKey] || 0) + cantidad;
          }
        }
      });
    }

    resultado[itemKey] = conteos;
  }
  return resultado;
}
